import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/macro'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { useWindowWidth } from 'core/ui/hooks/useWindowWidth'

import { ExpirationBar } from 'legacy/components/ExpirationBar/ExpirationBar'
import { intSizes } from 'legacy/styles/screen'
import { differenceInSeconds } from 'legacy/utils/date/differenceInSeconds'
import { formatTimestamp } from 'legacy/utils/date/formatTimestamp'

const TIMER_INTERVAL = 1000 * 60

const OfferExpirationBarContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  background-color: transparent;
`

const OfferExpirationBarLabel = styled.div`
  font-size: 13px;
  flex-shrink: 0;
  color: ${({ darkThemed }) =>
    darkThemed ? getToken('color-neutral-white') : getToken('color-neutral-dark')};
  margin-right: 8px;

  & > span {
    font-weight: 400;
  }
`

const OfferExpirationBarWrapper = styled.div`
  flex-shrink: 1;
  flex-grow: 0;
  width: 100%;
`

function OfferExpirationBar(props) {
  const { created_at, expires_at, onExpiration, darkThemed, forceMobile } = props
  const windowWidth = useWindowWidth()
  const isSmallScreen = windowWidth < intSizes.md

  const getRemainingTime = useCallback(
    () => differenceInSeconds(expires_at, Date.now()),
    [expires_at]
  )

  const [remainingSeconds, setRemainingSeconds] = useState(getRemainingTime())

  const getRemainingPercentage = useCallback(() => {
    const creationDate = new Date(created_at)
    const expirationDate = new Date(expires_at)

    const currentDiff = remainingSeconds
    const totalDiff = differenceInSeconds(expirationDate, creationDate)

    return Math.round((currentDiff / totalDiff) * 100)
  }, [created_at, expires_at, remainingSeconds])

  const [remainingPercentage, setRemainingPercentage] = useState(getRemainingPercentage())
  const timerRef = useRef()

  const startUpdatingRemainingTime = useCallback(() => {
    timerRef.current = window.setInterval(() => {
      setRemainingSeconds(getRemainingTime())
      setRemainingPercentage(getRemainingPercentage())
    }, TIMER_INTERVAL)
  }, [getRemainingPercentage, getRemainingTime])

  const stopUpdatingRemainingTime = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
    }
  }, [])

  const isExpired = useMemo(() => remainingSeconds <= 0, [remainingSeconds])

  const responsiveLabelText = useMemo(
    () =>
      forceMobile || isSmallScreen
        ? isExpired
          ? 'Expired'
          : 'Expires in'
        : isExpired
        ? 'Offer expired'
        : 'Offer expires in',
    [forceMobile, isExpired, isSmallScreen]
  )

  const formattedRemainingTime = useMemo(
    () => formatTimestamp(remainingSeconds),
    [remainingSeconds]
  )

  useEffect(() => {
    startUpdatingRemainingTime()
    return stopUpdatingRemainingTime
  }, [startUpdatingRemainingTime, stopUpdatingRemainingTime])

  useEffect(() => {
    if (isExpired) {
      onExpiration(true)
      stopUpdatingRemainingTime()
    }
  }, [isExpired, onExpiration, stopUpdatingRemainingTime])

  return (
    <OfferExpirationBarContainer>
      <OfferExpirationBarLabel darkThemed={darkThemed}>
        <span>{responsiveLabelText}</span>
        {isExpired ? '' : <strong> {formattedRemainingTime}</strong>}
      </OfferExpirationBarLabel>

      <OfferExpirationBarWrapper>
        <ExpirationBar
          remaining={remainingPercentage}
          ariaLabel={responsiveLabelText}
          forceMobile={forceMobile}
        />
      </OfferExpirationBarWrapper>
    </OfferExpirationBarContainer>
  )
}

OfferExpirationBar.propTypes = {
  created_at: PropTypes.instanceOf(Date).isRequired,
  expires_at: PropTypes.instanceOf(Date).isRequired,
  darkThemed: PropTypes.bool,
  forceMobile: PropTypes.bool,
  onExpiration: PropTypes.func,
}

OfferExpirationBar.defaultProps = {
  darkThemed: false,
  forceMobile: false,
  onExpiration: Function.prototype,
}

export { OfferExpirationBar }
